import { useState } from "react";
import { X, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useCurrency } from "@/context/CurrencyContext";

const TIERS = [
  {
    id: "annual",
    lookup_key: "premium_yearly",
    name: "Annual Contemplation Pass",
    badge: "MOST REVERED",
    recommended: true,
    testId: "tier-annual-card",
  },
  {
    id: "monthly",
    lookup_key: "premium_monthly",
    name: "Monthly Celestial Flow",
    badge: "FLEXIBLE",
    recommended: false,
    testId: "tier-monthly-card",
  },
];

const FEATURES = [
  "Unrestricted Love Focus & Money forecasts",
  "Full Weekly & Monthly deep-dive readings",
  "A completely ad-free experience",
  "Priority offline sync & unlimited archive",
  "Retrograde & planetary aspect alerts",
];

export default function SubscribeModal({ open, onClose, trigger }) {
  const [selected, setSelected] = useState("annual");
  const [loading, setLoading] = useState(false);
  const { currency, prices } = useCurrency();
  const sym = prices.symbol;

  if (!open) return null;

  const disp = (id) =>
    id === "annual"
      ? { price: `${sym}${prices.annualPerMonth}`, unit: "/mo", billing: `Billed annually at ${sym}${prices.annualTotal} · Save 33%` }
      : { price: `${sym}${prices.monthly}`, unit: "/mo", billing: "Billed monthly · cancel anytime" };

  const startCheckout = async () => {
    const tier = TIERS.find((t) => t.id === selected);
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup_key,
        currency,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-3 py-6">
      <div
        data-testid="modal-subscription"
        className="relative w-full max-w-md bg-[#0B0F19] border border-[#D4AF37]/25 rounded-3xl p-6 fade-up max-h-[92vh] overflow-y-auto"
      >
        <button
          data-testid="btn-close-subscription"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A7B0C0] hover:text-starlight transition-colors"
        >
          <X size={22} />
        </button>

        <p className="text-[11px] uppercase tracking-[0.3em] gold-text mb-2">Unlock the Cosmos</p>
        <h2 className="text-3xl font-light mb-1">Astra Premium</h2>
        <p className="text-[#A7B0C0] text-base mb-5">
          {trigger && !["premium", "the founding offer"].includes(trigger) ? `Unlock ${trigger} and more.` : `Unlock every reading from only ${sym}${prices.annualPerMonth}/month.`}
        </p>

        <div className="space-y-3 mb-5">
          {TIERS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testId}
              onClick={() => setSelected(t.id)}
              className={`w-full text-left rounded-2xl p-4 border transition-all ${
                selected === t.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-[#D4AF37]/15 bg-[#0F1523] hover:border-[#D4AF37]/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.25em] gold-text">{t.badge}</span>
                {t.recommended && (
                  <span className="text-[10px] uppercase tracking-wider text-[#04060B] bg-[#D4AF37] px-2 py-0.5 rounded-full">
                    Best value
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-3xl font-medium gold-glow-text">{disp(t.id).price}</span>
                <span className="text-[#A7B0C0]">{disp(t.id).unit}</span>
              </div>
              <p className="text-base mt-0.5">{t.name}</p>
              <p className="text-sm text-[#A7B0C0]">{disp(t.id).billing}</p>
            </button>
          ))}
        </div>

        <ul className="space-y-2 mb-6">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2 text-base text-[#D6D0C5]">
              <Check size={16} className="gold-text mt-1 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <button
          data-testid="btn-start-subscription"
          onClick={startCheckout}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={20} /> : "Begin Premium"}
        </button>
        <p className="text-center text-xs text-[#A7B0C0] mt-3">Secure checkout via Stripe · Test mode</p>
      </div>
    </div>
  );
}
